/**
 * PNG download utilities.
 */

import { generateExportFilename } from "./helpers";

/**
 * Delay in ms before revoking the object URL after triggering a download.
 * Gives browsers time to start the download before the URL is invalidated.
 */
const BLOB_URL_REVOKE_DELAY_MS = 100;

/**
 * Generate a PNG filename for the exported chart.
 * Format: {projectName}-YYYYMMDD-HHMMSS.png
 * Delegates to generateExportFilename for consistent date-stamp formatting.
 *
 * @param projectName - The project name (will be sanitized). Defaults to "untitled".
 * @returns A valid filename for the PNG export
 */
export function generateFilename(projectName?: string): string {
  // Short-circuit undefined/null/empty to "untitled" before delegating.
  // Whitespace-only names (e.g. "   ") are passed through and normalised to
  // "untitled" by generateExportFilename → sanitizeFilename downstream.
  return generateExportFilename(projectName || "untitled", "png");
}

/**
 * Download a blob as a file.
 * Creates a temporary anchor element and programmatically triggers a download.
 *
 * **Browser-only**: Requires `document` and `URL.createObjectURL` to be
 * available. Do not call in a server-side or non-browser environment.
 *
 * @param blob - The Blob to download
 * @param filename - Non-empty filename for the download (e.g. "chart-export.png").
 *   An empty or whitespace-only string is normalized to "download" as a safe fallback.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  // Normalize empty/whitespace filename to avoid browsers substituting an
  // unpredictable auto-generated name (behaviour varies by browser/OS).
  const safeFilename = filename.trim() || "download";

  // Guard: this function is a no-op outside a browser context (e.g. SSR, tests
  // that do not mock document). All real callers run in the browser.
  if (typeof document === "undefined") return;

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = safeFilename;

  // Append to body to ensure it works in all browsers.
  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    // DOM cleanup runs even if click() throws — isolated so its own errors
    // don't mask any exception thrown by click().
    document.body.removeChild(link);

    // Clean up the object URL after a short delay to give browsers time to
    // start the download before the URL is invalidated.
    // The timer handle is intentionally not stored — this is a fire-and-forget
    // cleanup; the module has no teardown lifecycle and the tiny delay is
    // harmless if the window is closed before it fires.
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, BLOB_URL_REVOKE_DELAY_MS);
  }
}

/**
 * Download a canvas as a PNG file.
 * Encodes the canvas contents as a PNG blob and triggers a browser download.
 *
 * **Browser-only**: Requires `document` and `URL.createObjectURL` to be
 * available (delegated to {@link downloadBlob}).
 *
 * @param canvas - The HTMLCanvasElement to export as PNG
 * @param filename - Optional filename for the download. Defaults to a
 *   timestamped name generated by {@link generateFilename}.
 * @returns A Promise that resolves when the download has been triggered
 *   (the browser download itself may continue asynchronously).
 * @throws {Error} If the browser cannot encode the canvas to a PNG blob
 *   (e.g. a tainted cross-origin canvas or memory pressure).
 */
export async function downloadCanvasAsPng(
  canvas: HTMLCanvasElement,
  filename?: string
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Failed to create PNG blob"));
        return;
      }

      const finalFilename = filename || generateFilename();
      downloadBlob(blob, finalFilename);
      resolve();
    }, "image/png");
  });
}
